/* ------------------------------------------------------------------
  File: IconButton.jsx
  Author: Engr. Shaffa K. Kokro Jr. - CEO, Accend Holdings Ltd.
  Last Modified: 2026-05-18
------------------------------------------------------------------ */

// Purpose:
//   - Provides a reusable icon-only button component for the Accend Holdings website.
//   - Supports native button actions, React Router navigation, and external links.
//   - Supports variants, sizes, disabled state, and accessible labels.
//   - Uses the acc-icon-button namespace to avoid conflicts with Bootstrap.
//   - Designed for compact actions such as menu toggles, search triggers,
//     carousel controls, close buttons, and social links.

/**
 * IconButton
 *
 * A reusable icon-only UI button component that can render as:
 * - a native <button> when no `to` or `href` is provided
 * - a React Router <Link> when `to` is provided
 * - a standard <a> link when `href` is provided
 *
 * @param {React.ReactNode} icon
 * The icon displayed inside the button.
 *
 * @param {string} ariaLabel
 * Accessible label for screen readers. Required for icon-only buttons.
 *
 * @param {"primary" | "secondary" | "outline" | "ghost" | "soft" | "inverse"} [variant="ghost"]
 * Controls the visual style of the icon button.
 *
 * @param {"sm" | "md" | "lg"} [size="md"]
 * Controls the icon button size.
 *
 * @param {string} [to]
 * Internal route path for React Router navigation.
 *
 * @param {string} [href]
 * External URL or standard anchor link.
 *
 * @param {"button" | "submit" | "reset"} [type="button"]
 * Native button type. Used only when rendering a <button>.
 *
 * @param {boolean} [disabled=false]
 * Disables the button. When disabled, link navigation is prevented by rendering a native disabled button.
 *
 * @param {string} [className=""]
 * Additional custom class names.
 *
 * @param {Function} [onClick]
 * Click handler for button actions.
 *
 * @param {string} [target]
 * Anchor target for external links. Example: "_blank".
 *
 * @param {string} [rel]
 * Anchor rel attribute. Automatically set to "noopener noreferrer" when target is "_blank".
 */

import { Link } from "react-router-dom";
import "./IconButton.css";

const IconButton = ({
  icon,
  ariaLabel,
  variant = "ghost",
  size = "md",
  to,
  href,
  type = "button",
  disabled = false,
  className = "",
  onClick,
  target,
  rel,
  ...props
}) => {
  const iconButtonClasses = [
    "acc-icon-button",
    `acc-icon-button--${variant}`,
    `acc-icon-button--${size}`,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const content = (
    <span className="acc-icon-button__icon" aria-hidden="true">
      {icon}
    </span>
  );

  if (to && !disabled) {
    return (
      <Link
        to={to}
        className={iconButtonClasses}
        aria-label={ariaLabel}
        {...props}
      >
        {content}
      </Link>
    );
  }

  if (href && !disabled) {
    return (
      <a
        href={href}
        className={iconButtonClasses}
        aria-label={ariaLabel}
        target={target}
        rel={target === "_blank" ? "noopener noreferrer" : rel}
        {...props}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      type={type}
      className={iconButtonClasses}
      disabled={disabled}
      onClick={onClick}
      aria-label={ariaLabel}
      {...props}
    >
      {content}
    </button>
  );
};

export default IconButton;